import { C } from '@parsina/framework/clinical/src/constants/theme';
import { useKeyboardInset } from '@parsina/framework';
import useResponsive from '@parsina/framework/clinical/src/hooks/useResponsive';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';

// ── The pre-router chrome ─────────────────────────────────────────────────────
//
// The backdrop, the card and the branded header the doctor sees BEFORE the app
// shell exists. It was inline in `Onboarding/index.jsx` until the prize wheel
// needed the same frame: the wheel has a second mount point (`screens/PrizeWheel`,
// the boot-time recovery screen from ONBOARDING_PRIZE_WHEEL_PLAN.md §5.3) which
// renders in exactly the same place in App.jsx, for exactly the same reason —
// there is no router yet. Two copies of a fixed-position full-bleed layout is
// two places for a phone fix to be applied once.
//
// This is a SHELL, not a screen: it owns the frame and nothing inside it. The
// wizard keeps its stage machine and its progress bar; the wheel brings its own.
export default function OnboardingShell({ subtitle, children }) {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const keyboardInset = useKeyboardInset();

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100,
      background: `linear-gradient(145deg, ${C.navyDark} 0%, ${C.navy} 55%, ${C.navyLight} 100%)`,
      display: 'flex',
      // On a phone the card IS the screen, so it starts at the top rather than
      // floating in the middle of a viewport it fills anyway.
      // `flex-start`, NOT `stretch`. `stretch` gives the card a DEFINITE cross
      // size — the backdrop's own height, which `min-height` can only match,
      // never exceed (flexbox §9.4: a single-line flex container with a
      // definite cross size hands that size to its line). The card also hides
      // its overflow, for its corners, so a step taller than the window had
      // its bottom CLIPPED with no scroll anywhere to reach it — the backdrop
      // is `overflow-y: auto`, but its only child was never taller than the
      // backdrop itself. The استعلام result is exactly what makes step 1 that
      // tall. Aligning to the start leaves the card's height content-driven,
      // so it grows and the backdrop scrolls.
      alignItems: isMobile ? 'flex-start' : 'center',
      justifyContent: 'center',
      fontFamily: 'var(--app-font)',
      // §H.1 — the docked «ادامه» has to clear the software keyboard, and every
      // step of this flow is a form. `dvh`, not `vh`.
      padding: isMobile
        ? `0 0 ${keyboardInset}px`
        : '24px 16px',
      overflowY: 'auto',
    }}>
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(24px) }
          to   { opacity: 1; transform: translateY(0) }
        }
      `}</style>

      <div style={{
        background: 'white',
        // 520px on a 360px screen is a card wider than the window. On a phone
        // it stops being a card: full width, no corner, no shadow — there is
        // nothing behind it to float above.
        borderRadius: isMobile ? 0 : 18,
        width: '100%',
        maxWidth: isMobile ? '100%' : 520,
        boxShadow: isMobile ? 'none' : '0 40px 100px rgba(0,0,0,0.35)',
        animation: isMobile ? 'none' : 'slideUp 0.4s cubic-bezier(0.22,1,0.36,1) both',
        overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        // `100%`, not `100dvh`: the backdrop is `fixed; inset: 0` with the
        // keyboard inset as its bottom padding, so its content box already IS
        // the space left for the card. `100dvh` measures the whole window and
        // would hang the card's last rem underneath the keyboard.
        minHeight: isMobile ? '100%' : undefined,
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : undefined,
      }}>
        {/* Header */}
        <div style={{
          background: `linear-gradient(160deg, ${C.navyDark} 0%, ${C.navy} 60%, ${C.navyLight} 100%)`,
          padding: '22px 28px 18px',
          display: 'flex', alignItems: 'center', gap: 14,
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: 'rgba(255,255,255,0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <svg viewBox="0 0 24 24" fill="rgba(255,255,255,0.85)" width="20" height="20">
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z"/>
            </svg>
          </div>
          <div>
            <div style={{ fontSize: '0.9375rem', fontWeight: 800, color: 'white' }}>{t('auth.onboarding.pageTitle')}</div>
            <div style={{ fontSize: '0.6875rem', color: 'rgba(255,255,255,0.55)' }}>{subtitle}</div>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '26px 28px 32px' }}>
          {children}
        </div>
      </div>
    </div>
  );
}
